/**
 * Level 1 outcome card — a full-width button. Vertical order is the SPEC §5.4
 * contract: name · band pill · score · gauge · records-by-band · volume ·
 * detriment mix · open. Histogram scaling (`maxBand`) is shared across the row.
 */

import { BAND_COLORS, bandOf } from '../constants';
import type { OutcomeCardData } from '../types';
import { recordsLabel } from '../utils';
import { BandGauge, BandHistogram, BandPill, Delta, DetrimentMixBar } from './parts';

const SECTION_LABEL =
  'mt-3.5 border-t border-[var(--soft,#ECECEE)] pt-3 text-[9px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground';

export function OutcomeCard({
  outcome,
  maxBand,
  onOpen,
}: {
  outcome: OutcomeCardData;
  maxBand: number;
  onOpen: (outcomeId: string) => void;
}) {
  const hasRecords = outcome.volume > 0;
  const band = bandOf(outcome.score);
  return (
    <button
      type="button"
      onClick={() => onOpen(outcome.id)}
      data-testid={`outcome-card-${outcome.id}`}
      className="flex flex-col rounded-[var(--radius)] border border-border bg-background p-[1.1rem] text-left transition-all hover:border-[var(--go-good-vibes)] hover:shadow-[0_2px_8px_rgba(67,66,245,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--go-good-vibes)]"
    >
      <div className="min-h-[2.4em] text-[13px] font-extrabold leading-[1.3] tracking-[-0.005em]">
        {outcome.name}
      </div>

      <div className="mt-0.5">
        <BandPill score={outcome.score} />
      </div>

      <div className="mt-2 flex items-baseline gap-1">
        <span
          className="text-[2.375rem] font-extrabold leading-none tracking-[-0.04em]"
          style={{ color: hasRecords ? BAND_COLORS[band] : 'hsl(var(--muted-foreground))' }}
        >
          {hasRecords ? outcome.score.toFixed(1) : '—'}
        </span>
        {hasRecords && <Delta cur={outcome.score} prev={outcome.scorePrev} dp={1} />}
      </div>

      <BandGauge score={outcome.score} />

      <div className={SECTION_LABEL}>Records by band</div>
      <div className="mt-1 flex items-center justify-between text-[11px] font-bold text-muted-foreground">
        <span>{recordsLabel(outcome.volume)}</span>
        <Delta cur={outcome.volume} prev={outcome.volumePrev} pct neutral />
      </div>
      <div className="mt-2">
        <BandHistogram bands={outcome.bands} max={maxBand} height={42} />
      </div>

      <div className={SECTION_LABEL}>Detriment mix</div>
      <DetrimentMixBar mix={outcome.detrimentMix} />

      <div className="mt-3 text-[10px] font-extrabold tracking-wide text-[var(--go-good-vibes)]">
        Open outcome →
      </div>
    </button>
  );
}
